import { SimpleSeriesVector, SmoothingDataInput } from '../types';

const WEIGHTS_SUM_IN_REFERENCE_WINDOW = 0.95;

export function emaSmoothing(
  smoothingDataInput: SmoothingDataInput,
  // For EMA, the parameter is calculated so that weights of points inside
  // the window are 0.95 of total weight.
  smoothingWindowSize: number,
): SimpleSeriesVector[] {
  const { dataLength, seriesCount, getXValue, getBucket } = smoothingDataInput;

  const xScalingFactor = 1 / smoothingWindowSize;
  // We don't know how dense are the points in the smoothing window, so we
  // can't calculate the decay factor that would give sum of weights in the
  // window exactly equal to WEIGHTS_SUM_IN_REFERENCE_WINDOW * <sum of all weights>.
  //
  // However, we know that the limit of this ideal decay factor converges to
  // 1 - WEIGHTS_SUM_IN_REFERENCE_WINDOW when point density goes to infinity.
  //
  // Theoretically this decay factor is going to be too aggressive if there are
  // only a few points in the window, but the difference is negligible in practice.
  const decayFactor = 1 - WEIGHTS_SUM_IN_REFERENCE_WINDOW;

  let lastX = getXValue(0) ?? NaN;
  const lastYs: number[] = new Array(seriesCount + 1).fill(0);
  const totalWeights: number[] = new Array(seriesCount + 1).fill(0);

  return new Array(dataLength).fill(undefined).map((_, i): SimpleSeriesVector => {
    const x = getXValue(i);
    const changeInX = xScalingFactor * (x - lastX);
    lastX = x;

    const decay = Math.pow(decayFactor, changeInX); // decay over changeInX.

    return new Array(seriesCount + 1).fill(undefined).map((_, k) => {
      if (k === 0) {
        return getXValue(i);
      }

      const bucket = getBucket(k - 1, i);

      // Weights of all previous points diminish by decay. We need to apply this
      // decay regardless of whether the bucket is null or not - this way we don't
      // need to track last X values for each series separately, but can apply the
      // same decay uniformly.
      totalWeights[k] *= decay;
      lastYs[k] *= decay;

      if (bucket == null || bucket.count === 0) {
        return null;
      }

      if (!Number.isFinite(bucket.sum)) {
        return bucket.sum;
      }

      // New points gets weight 1 each. We simulate adding all bucket points
      // to the EMA, hence we use bucketSum and bucketCount.
      const totalWeight = (totalWeights[k] += bucket.count);
      lastYs[k] = lastYs[k] + bucket.sum;

      const smoothedY = lastYs[k] / totalWeight;

      return smoothedY;
    }) satisfies (number | null)[] as SimpleSeriesVector;
  });
}
